// frontend/pages/admin/EditProductsPage.jsx

import React, { useState, useEffect, useRef } from 'react';
import { useProductStore } from '../store/slices/productSlice';
import { useGetPaginatedProducts, useUpdateProductRecommendedList, useUpdateProductAccessoriesList, useGetProductsByIds, useSearchProductByName } from '../hooks/useProducts';
import { FaCircle } from 'react-icons/fa';
import { IoArrowBack } from 'react-icons/io5';
import { MdOutlinePlaylistAddCheck, MdOutlineLibraryAddCheck } from 'react-icons/md';
import toast, { Toaster } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const EditProductsPage = () => {
    const navigate = useNavigate();
    const {
        allGottenProducts,
        setAllGottenProducts,
        addMoreGottenProducts,
        currentProductRecommendedList,
        setCurrentProductRecommendedList,
        currentProductAccessoriesList,
        setCurrentProductAccessoriesList,
    } = useProductStore();

    const [leftSelectedProduct, setLeftSelectedProduct] = useState(null);
    const [page, setPage] = useState(0);
    const [mode, setMode] = useState('Recommended');
    const [leftSearchQuery, setLeftSearchQuery] = useState('');
    const [rightSearchQuery, setRightSearchQuery] = useState('');
    const loadMoreRef = useRef(null);
    const loadingMoreRef = useRef(false);

    const getPaginatedProductsQuery = useGetPaginatedProducts(page);
    const updateRecommendedListMutation = useUpdateProductRecommendedList();
    const updateAccessoriesListMutation = useUpdateProductAccessoriesList();

    const { data: recommendedProductsData, isLoading: isLoadingRecommended } = useGetProductsByIds(
        leftSelectedProduct?.recommended || []
    );

    const { data: accessoriesProductsData, isLoading: isLoadingAccessories } = useGetProductsByIds(
        leftSelectedProduct?.accessories || []
    );

    const { data: leftSearchData, refetch: refetchLeftSearch, isLoading: isLoadingLeftSearch } = useSearchProductByName(leftSearchQuery);
    const { data: rightSearchData, isLoading: isLoadingRightSearch } = useSearchProductByName(rightSearchQuery);

    // Intersection Observer for "load more" functionality
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                const target = entries[0];
                if (target.isIntersecting && !loadingMoreRef.current) {
                    loadingMoreRef.current = true;
                    setPage((prevPage) => prevPage + 1);
                }
            },
            {
                root: null,
                rootMargin: '0px 0px 200px 0px', // Adds space below the list to trigger early
                threshold: 0,
            }
        );

        const currentRef = loadMoreRef.current;
        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, []);

    // Handle fetching initial products and adding more on scroll
    useEffect(() => {
        if (getPaginatedProductsQuery.data) {
            if (page === 0) {
                setAllGottenProducts(getPaginatedProductsQuery.data);
            } else {
                addMoreGottenProducts(getPaginatedProductsQuery.data);
            }
            loadingMoreRef.current = false;
        }
    }, [getPaginatedProductsQuery.data, page, setAllGottenProducts, addMoreGottenProducts]);

    useEffect(() => {
        if (leftSelectedProduct) {
            if (mode === 'Recommended' && recommendedProductsData) {
                setCurrentProductRecommendedList(recommendedProductsData);
            } else if (mode === 'Accessories' && accessoriesProductsData) {
                setCurrentProductAccessoriesList(accessoriesProductsData);
            }
        }
    }, [leftSelectedProduct, recommendedProductsData, accessoriesProductsData, mode, setCurrentProductRecommendedList, setCurrentProductAccessoriesList]);

    // Handle left side product search
    useEffect(() => {
      if (leftSearchData) {
        setLeftSelectedProduct(leftSearchData);
        refetchLeftSearch();
      }
    }, [leftSearchData, refetchLeftSearch]);


    const handleLeftProductClick = (product) => {
        if (leftSelectedProduct?.id === product.id) {
            setLeftSelectedProduct(null);
        } else {
            setLeftSelectedProduct(product);
        }
    };

    const handleRightProductClick = (product) => {
        if (mode === 'Recommended') {
            const isSelected = currentProductRecommendedList.some(item => item.id === product.id);
            if (isSelected) {
                setCurrentProductRecommendedList(currentProductRecommendedList.filter(item => item.id !== product.id));
            } else {
                setCurrentProductRecommendedList([...currentProductRecommendedList, product]);
            }
        } else { // Accessories mode
            const isSelected = currentProductAccessoriesList.some(item => item.id === product.id);
            if (isSelected) {
                setCurrentProductAccessoriesList(currentProductAccessoriesList.filter(item => item.id !== product.id));
            } else {
                setCurrentProductAccessoriesList([...currentProductAccessoriesList, product]);
            }
        }
    };

    const handleConfirmUpdate = () => {
        if (!leftSelectedProduct) {
            toast.error('Please select a product on the left side to update.');
            return;
        }

        if (mode === 'Recommended') {
            const recommendedIds = currentProductRecommendedList.map(p => p.id);
            updateRecommendedListMutation.mutate({ productId: leftSelectedProduct.id, recommendedIds }, {
                onSuccess: () => {
                    toast.success('Recommended list updated successfully!');
                    setLeftSelectedProduct(null);
                    setCurrentProductRecommendedList([]);
                },
                onError: (error) => {
                    console.error('Update failed:', error);
                    toast.error('Failed to update recommended list.');
                }
            });
        } else { // Accessories mode
            const accessoriesIds = currentProductAccessoriesList.map(p => p.id);
            updateAccessoriesListMutation.mutate({ productId: leftSelectedProduct.id, accessoriesIds }, {
                onSuccess: () => {
                    toast.success('Accessories list updated successfully!');
                    setLeftSelectedProduct(null);
                    setCurrentProductAccessoriesList([]);
                },
                onError: (error) => {
                    console.error('Update failed:', error);
                    toast.error('Failed to update accessories list.');
                }
            });
        }
    };

    const productsToDisplay = leftSearchData ? [leftSearchData] : allGottenProducts;
    const rightListToDisplay = rightSearchData ? [rightSearchData] : allGottenProducts;

    const isProductInAnyList = (product) => {
        return (product?.recommended?.length > 0 || product?.accessories?.length > 0);
    };

    return (
        <div className="p-4">
            <Toaster />
            <div className="flex items-center mb-4">
                <button 
                    onClick={() => navigate(-1)} 
                    className="mr-4 p-2 rounded-full hover:bg-gray-200 transition-colors"
                >
                    <IoArrowBack size={24} />
                </button>
                <h1 className="text-2xl font-bold">Edit Product Recommendations & Accessories</h1>
            </div>
            
            <div className="flex justify-end mb-4">
                <button
                    onClick={() => setMode('Recommended')}
                    className={`p-2 rounded-l-lg border ${mode === 'Recommended' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-800'}`}
                >
                    <div className='flex justify-center items-center gap-2'>
                        Recommended
                        <MdOutlinePlaylistAddCheck size={20}/>
                    </div>
                </button>
                <button
                    onClick={() => setMode('Accessories')}
                    className={`p-2 rounded-r-lg border ${mode === 'Accessories' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-800'}`}
                >
                     <div className='flex justify-center items-center gap-2'>
                        Accessories
                        <MdOutlineLibraryAddCheck size={20} />
                    </div>
                </button>
            </div>

            <div className="flex space-x-4">
                {/* Left Side: Select Product to Edit */}
                <div className="w-1/2 border p-4 rounded-lg shadow-md">
                    <h2 className="text-xl font-semibold mb-2">Select Product to Edit</h2>
                    <input
                        type="text"
                        placeholder="Search product..."
                        value={leftSearchQuery}
                        onChange={(e) => setLeftSearchQuery(e.target.value)}
                        className="w-full p-2 mb-4 border rounded"
                    />
                    
                    <div className="h-[600px] overflow-y-auto">
                        {isLoadingLeftSearch ? (
                            <p>Searching...</p>
                        ) : (
                            productsToDisplay.map((product) => (
                                <div
                                    key={product.id}
                                    onClick={() => handleLeftProductClick(product)}
                                    className={`p-4 mb-2 rounded cursor-pointer transition-colors relative ${
                                        leftSelectedProduct?.id === product.id ? 'bg-blue-100 border-blue-500 border-2' : 'bg-gray-100 border'
                                    }`}
                                >
                                    {isProductInAnyList(product) && (
                                        <FaCircle className="absolute top-2 left-2 text-blue-500 text-xs" />
                                    )}
                                    <h3 className="font-medium">{product.name}</h3>
                                    <p className="text-sm text-gray-600">{product.brief_details}</p>
                                </div>
                            ))
                        )}
                        <div ref={loadMoreRef} style={{ height: '200px' }}>
                            {getPaginatedProductsQuery.isFetching && <p>Loading more products...</p>}
                        </div>
                    </div>
                </div>

                {/* Right Side: Select Recommended/Accessories */}
                <div className="w-1/2 border p-4 rounded-lg shadow-md">
                    <h2 className="text-xl font-semibold mb-2">
                        {mode === 'Recommended' ? 'Recommended' : 'Accessories'} List
                    </h2>
                    <input
                        type="text"
                        placeholder="Search products to add..."
                        value={rightSearchQuery}
                        onChange={(e) => setRightSearchQuery(e.target.value)}
                        className="w-full p-2 mb-4 border rounded"
                    />

                    <div className="h-[600px] overflow-y-auto">
                        {(isLoadingRecommended || isLoadingAccessories || isLoadingRightSearch) ? (
                            <p>Loading products...</p>
                        ) : (
                            rightListToDisplay.map((product) => {
                                const isSelected = (mode === 'Recommended' && currentProductRecommendedList.some(item => item.id === product.id)) ||
                                    (mode === 'Accessories' && currentProductAccessoriesList.some(item => item.id === product.id));
                                
                                return (
                                    <div
                                        key={product.id}
                                        onClick={() => handleRightProductClick(product)}
                                        className={`p-4 mb-2 rounded cursor-pointer transition-colors ${
                                            isSelected ? 'bg-green-100 border-green-500 border-2' : 'bg-gray-100 border'
                                        }`}
                                    >
                                        <h3 className="font-medium">{product.name}</h3>
                                        <p className="text-sm text-gray-600">{product.brief_details}</p>
                                    </div>
                                );
                            })
                        )}
                    </div>
                    
                    <button
                        onClick={handleConfirmUpdate}
                        disabled={!leftSelectedProduct || updateRecommendedListMutation.isLoading || updateAccessoriesListMutation.isLoading}
                        className={`w-full mt-4 p-3 rounded text-white font-bold transition-colors ${
                            leftSelectedProduct ? 'bg-blue-500 hover:bg-blue-600' : 'bg-gray-400 cursor-not-allowed'
                        }`}
                    >
                        {updateRecommendedListMutation.isLoading || updateAccessoriesListMutation.isLoading ? 'Updating...' : 'Confirm Update'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditProductsPage;